<template>
  <BottomSheet :show="show" @close="$emit('close')">
    <div class="px-6 py-4">
      <h2 class="text-2xl font-bold text-center mb-2">Seleziona età scenica</h2>
      <p class="text-gray-500 text-center mb-4">
        Scegli un intervallo di età scenica
      </p>
      <!-- Custom Range Slider -->
      <div class="relative w-full h-8 mb-4" @mousedown="onTrackClick">
        <!-- Track -->
        <div class="absolute top-1/2 w-full h-2 bg-gray-200 rounded-full transform -translate-y-1/2"></div>
        
        <!-- Active Range -->
        <div 
          class="absolute top-1/2 h-2 bg-primary rounded-full transform -translate-y-1/2 pointer-events-none"
          :style="{ 
            left: `${((localRange[0] - min) / (max - min)) * 100}%`, 
            width: `${((localRange[1] - localRange[0]) / (max - min)) * 100}%` 
          }"
        ></div>
        
        <!-- Min Handle -->
        <div 
          class="absolute top-1/2 w-6 h-6 bg-white border-2 border-primary rounded-full shadow-lg cursor-grab transform -translate-y-1/2 -translate-x-1/2 hover:border-primary-dark transition-colors select-none z-10"
          :class="{ 'cursor-grabbing': isDragging && dragIndex === 0 }"
          :style="{ left: `${((localRange[0] - min) / (max - min)) * 100}%` }"
          @mousedown.stop="startDrag(0, $event)"
          @touchstart.stop="startDrag(0, $event)"
        ></div>
        
        <!-- Max Handle -->
        <div 
          class="absolute top-1/2 w-6 h-6 bg-white border-2 border-primary rounded-full shadow-lg cursor-grab transform -translate-y-1/2 -translate-x-1/2 hover:border-primary-dark transition-colors select-none z-10"
          :class="{ 'cursor-grabbing': isDragging && dragIndex === 1 }"
          :style="{ left: `${((localRange[1] - min) / (max - min)) * 100}%` }"
          @mousedown.stop="startDrag(1, $event)"
          @touchstart.stop="startDrag(1, $event)"
        ></div>
      </div>
      
      <div class="flex justify-between mt-2 text-sm text-gray-600">
        <span>{{ localRange[0] }}</span>
        <span>{{ localRange[1] }}</span>
      </div>
      <div class="flex gap-3 mt-6">
        <CuButton dark class="flex-1" @click="$emit('close')">Annulla</CuButton>
        <CuButton primary class="flex-1" @click="confirm">Conferma</CuButton>
      </div>
    </div>
  </BottomSheet>
</template>

<script>
import BottomSheet from './BottomSheet.vue'
import CuButton from '../cu/CuButton.vue'

export default {
  name: "EtaScenicaSheet",
  components: { BottomSheet, CuButton },
  props: {
    show: { type: Boolean, default: false },
    min: { type: Number, required: true },
    max: { type: Number, required: true },
    value: { type: Array, default: () => [0, 0] }
  },
  data() {
    return {
      localRange: this.value && this.value.length === 2 ? [...this.value] : [this.min, this.max],
      isDragging: false,
      dragIndex: null
    }
  },
  watch: {
    value(newVal) {
      if (Array.isArray(newVal) && newVal.length === 2) {
        this.localRange = [...newVal];
      }
    },
    show(val) {
      if (val && Array.isArray(this.value) && this.value.length === 2) {
        this.localRange = [...this.value];
      }
    }
  },
  methods: {
    confirm() {
      this.$emit('confirm', [...this.localRange]);
    },
    
    onTrackClick(event) {
      // Don't handle if clicking on a handle
      if (event.target !== event.currentTarget) return;
      
      const rect = event.currentTarget.getBoundingClientRect();
      const percentage = (event.clientX - rect.left) / rect.width;
      const value = Math.round(this.min + percentage * (this.max - this.min));
      
      // Find which handle is closer and move that one
      const distToMin = Math.abs(value - this.localRange[0]);
      const distToMax = Math.abs(value - this.localRange[1]);
      
      if (distToMin <= distToMax) {
        this.localRange[0] = Math.min(value, this.localRange[1]);
      } else {
        this.localRange[1] = Math.max(value, this.localRange[0]);
      }
    },
    
    startDrag(index, event) {
      this.isDragging = true;
      this.dragIndex = index;
      
      const slider = event.currentTarget.parentElement;
      const rect = slider.getBoundingClientRect();
      
      const handleMove = (e) => {
        if (!this.isDragging) return;
        
        const currentX = e.type.includes('touch') ? e.touches[0].clientX : e.clientX;
        const percentage = Math.max(0, Math.min(1, (currentX - rect.left) / rect.width));
        const value = Math.round(this.min + percentage * (this.max - this.min));
        
        this.$set(this.localRange, index, index === 0 
          ? Math.min(value, this.localRange[1])
          : Math.max(value, this.localRange[0])
        );
      };
      
      const handleEnd = () => {
        this.isDragging = false;
        this.dragIndex = null;
        
        document.removeEventListener('mousemove', handleMove);
        document.removeEventListener('mouseup', handleEnd);
        document.removeEventListener('touchmove', handleMove);
        document.removeEventListener('touchend', handleEnd);
        
        document.body.style.userSelect = '';
        document.body.style.touchAction = '';
      };
      
      // Disable text selection and scrolling
      document.body.style.userSelect = 'none';
      document.body.style.touchAction = 'none';
      
      // Add event listeners
      document.addEventListener('mousemove', handleMove, { passive: false });
      document.addEventListener('mouseup', handleEnd);
      document.addEventListener('touchmove', handleMove, { passive: false });
      document.addEventListener('touchend', handleEnd);
      
      // Prevent default behavior
      event.preventDefault();
    }
  }
}
</script>
